// frontend/src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { loginUser, logoutUser, refreshToken } from '../api/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const refreshTimerRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

  // Decode a JWT without verifying signature (for client-side convenience)
  const decodeJwt = (token) => {
    try {
      const [, payload] = token.split('.');
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(json);
    } catch (_) {
      return null;
    }
  };

  // Define logout before scheduleRefresh so it can be referenced safely
  const logout = useCallback(async () => {
    try {
      await logoutUser();
      setIsLoggedIn(false);
      setUser(null);
      setUserType(null);
      console.log('Logged out on frontend');
    } catch (error) {
      console.error('Logout failed on frontend:', error);
      setIsLoggedIn(false);
      setUser(null);
      setUserType(null);
    }
    // Cleanup
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    localStorage.removeItem('jwtToken');
  }, []);

  const scheduleRefresh = useCallback((token) => {
    if (!token) return;
    try {
      const payload = decodeJwt(token);
      if (!payload || !payload.exp) return;
      const now = Math.floor(Date.now() / 1000);
      // Refresh 5 minutes before expiry
      const leadSeconds = 5 * 60;
      const delayMs = Math.max((payload.exp - now - leadSeconds) * 1000, 0);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      const id = setTimeout(async () => {
        try {
          // Always refresh token - persistent session even when inactive
          console.log('[Auth] Auto-refreshing token...');
          await refreshToken();
        } catch (err) {
          // If refresh fails, force logout
          console.error('[Auth] Token refresh failed:', err);
          await logout();
        }
      }, delayMs);
      refreshTimerRef.current = id;
      console.log(`[Auth] Token refresh scheduled in ${Math.round(delayMs / 1000 / 60)} minutes`);
    } catch (_) {
      // Ignore scheduling errors
    }
  }, [logout]);

  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (token) {
      const payload = decodeJwt(token);
      const nowInSec = Math.floor(Date.now() / 1000);
      if (payload && (!payload.exp || payload.exp > nowInSec)) {
        setIsLoggedIn(true);
        const nextUsername = payload.user_id || 'user';
        setUser((prev) => (prev?.username === nextUsername ? prev : { username: nextUsername }));
        setUserType(payload.user_type || null);
        scheduleRefresh(token);
      } else {
        // Expired/invalid token — clean up
        localStorage.removeItem('jwtToken');
        setIsLoggedIn(false);
        setUser(null);
        setUserType(null);
      }
    }
    setIsAuthReady(true);
  }, [scheduleRefresh]);

  // Inactivity timeout — logout after 5 minutes of no user activity
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      const token = localStorage.getItem('jwtToken');
      if (token) {
        console.log('[Auth] User inactive for 5 minutes, logging out...');
        logout();
      }
    }, INACTIVITY_TIMEOUT_MS);
  }, [logout, INACTIVITY_TIMEOUT_MS]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    const handleActivity = () => resetInactivityTimer();

    events.forEach((event) => window.addEventListener(event, handleActivity, { passive: true }));
    resetInactivityTimer(); // start timer on login

    return () => {
      events.forEach((event) => window.removeEventListener(event, handleActivity));
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [isLoggedIn, resetInactivityTimer]);

  // Listen for token refresh events to reschedule the timer and update user payload
  useEffect(() => {
    const handler = () => {
      const token = localStorage.getItem('jwtToken');
      if (!token) return;
      const payload = decodeJwt(token);
      if (payload) {
        setIsLoggedIn(true);
        const nextUsername = payload.user_id || 'user';
        setUser((prev) => (prev?.username === nextUsername ? prev : { username: nextUsername }));
        setUserType(payload.user_type || null);
        scheduleRefresh(token);
      }
    };
    window.addEventListener('tokenRefreshed', handler);
    return () => window.removeEventListener('tokenRefreshed', handler);
  }, [scheduleRefresh]);

  const login = async (username, password) => {
    try {
      const data = await loginUser(username, password);

      setIsLoggedIn(true);
      setUser({ username });
      // Extract user_type from JWT token (backend doesn't include it in response)
      const token = localStorage.getItem('jwtToken');
      let tokenUserType = null;
      if (token) {
        const payload = decodeJwt(token);
        tokenUserType = payload?.user_type || null;
        scheduleRefresh(token);
      }
      setUserType(tokenUserType);
      console.log('Login successful on frontend');
      return true;
    } catch (error) {
      console.error('Login failed on frontend:', error);
      setIsLoggedIn(false);
      setUser(null);
      setUserType(null);
      throw error;
    }
  };

  // logout defined above

  // Computed admin check
  const isAdmin = userType === 'admin';

  return (
    <AuthContext.Provider value={{ isLoggedIn, user, userType, isAdmin, login, logout, isAuthReady }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
